import { Add, ArrowBack, ArrowLeft, Search } from '@mui/icons-material'
import {
  Box,
  Hidden,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import DocumentAdminFolderCard from 'components/admin/docs-admin-folder-card'
import DocsAdminFolderModal from 'components/admin/docs-admin-folder-modal'
import {
  Operations as DocumentAdminOperations,
  operations as documentAdminOperations
} from 'states/dux/document-admin/operations'
import { Payload as DocumentAdminPayload } from 'states/dux/document-admin/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFile } from 'types/nivos-file'
import { NivosFolder } from 'types/nivos-folder'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
  documentAdmin: DocumentAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
    documentAdmin: DocumentAdminOperations
  }
}

interface OwnProps {
  onCloseContent: () => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const DocumentsAdminFC: React.FC<any> = (props: AppProps) => {
  const { theme, onCloseContent, user, documentAdmin, operations } = props
  const [selectedFolder, setSelectedFolder] = React.useState<NivosFolder>()
  const [searchKey, setSearchKey] = React.useState('')
  const [filtered, setFiltered] = React.useState(false)
  const isMobileView = useMediaQuery('(max-width:600px)')

  const _filterData = (): void => {
    void get(`/document/?search=${searchKey}`).then((data: NivosFolder[]) => {
      try {
        operations.documentAdmin.set(
          data.map(item => objectifyDateStringFields(item))
        )
        setFiltered(searchKey.trim() !== '')
      } catch (e) {
        console.log(e)
      }
    })
  }

  const emptyFolderData: NivosFolder = {
    id: 0,
    name: '',
    groupsAccess: [],
    usersAccess: [],
    prohibitedUsers: [],
    files: [] as NivosFile[],
    createdBy: user,
    createdAt: new Date()
  }

  React.useEffect(() => {
    void get('/document/').then((data: NivosFolder[]) => {
      try {
        operations.documentAdmin.set(
          data.map(item => objectifyDateStringFields(item))
        )
      } catch (e) {
        console.log(e)
      }
    })
  }, [])

  return (
    <Box
      border={1}
      borderRadius={'12px'}
      borderColor={theme.secondaryColor}
      padding={isMobileView ? 1 : 2}
      width={0.9}
      minHeight={isMobileView ? '95vh' : '85vh'}
      marginX={'auto'}
    >
      <DocsAdminFolderModal
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
      />
      <Box display={'flex'} alignItems={'center'}>
        <IconButton
          size={'medium'}
          onClick={() => {
            // Fetch new if current results are filtered
            if (filtered) {
              void get('/document/').then((data: NivosFolder[]) => {
                try {
                  operations.documentAdmin.set(
                    data.map(item => objectifyDateStringFields(item))
                  )
                } catch (e) {
                  console.log(e)
                }
              })
            }
            onCloseContent()
          }}
        >
          <ArrowBack fontSize={'large'} />
        </IconButton>
        <Box
          paddingLeft={1}
          borderRight={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Box
          paddingRight={2}
          borderLeft={1}
          height={'56px'}
          color={theme.secondaryColor}
        />
        <Hidden smDown>
          <Typography variant={'h3'}>Documents Admin</Typography>
        </Hidden>
        <Box flexGrow={1} />
        <Box>
          <TextField
            id={'search-user-name'}
            InputProps={{
              startAdornment: <Search />
            }}
            type={'text'}
            placeholder={'Document name...'}
            value={searchKey}
            onChange={event => setSearchKey(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.code === 'Enter') {
                _filterData()
              }
            }}
          />
        </Box>
        <Tooltip title={'Create new folder'} placement={'top'}>
          <IconButton
            sx={{ margin: 1 }}
            onClick={() => setSelectedFolder(emptyFolderData)}
          >
            <Add fontSize={'large'} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box padding={isMobileView ? 1 : 4}>
        <Hidden smDown>
          <Box display={'flex'} flexWrap={'wrap'} padding={4} paddingX={8}>
            {documentAdmin.data.map((folderItem, index) => (
              <DocumentAdminFolderCard
                key={index}
                folderDetails={folderItem}
                onClickEvent={selectedFolder =>
                  setSelectedFolder(selectedFolder)
                }
              />
            ))}
            {documentAdmin.data.length === 0 && (
              <Typography variant="h3" align="center" marginY={2}>
                No Data
              </Typography>
            )}
          </Box>
        </Hidden>
        <Hidden smUp>
          <Box display={'flex'} flexDirection={'column'}>
            {documentAdmin.data.map((folderItem, index) => (
              <Box
                key={index}
                display={'flex'}
                borderBottom={1}
                width={1}
                sx={{ cursor: 'pointer' }}
                alignItems={'center'}
                onClick={() => setSelectedFolder(folderItem)}
              >
                <IconButton>
                  <ArrowLeft />
                </IconButton>
                <Typography paddingLeft={1} flexGrow={1}>
                  {folderItem.name}
                </Typography>
                <Box
                  paddingLeft={1}
                  borderRight={1}
                  height={'24px'}
                  color={theme.secondaryColor}
                />
                <Box
                  paddingRight={1}
                  borderLeft={1}
                  height={'24px'}
                  color={theme.secondaryColor}
                />
                <Typography variant={'caption'} width={45}>
                  Files: {folderItem.files.length}
                </Typography>
              </Box>
            ))}
            {documentAdmin.data.length === 0 && (
              <Typography variant="h3" align="center" marginY={2}>
                No Data
              </Typography>
            )}
          </Box>
        </Hidden>
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin,
    documentAdmin: state.documentAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations,
  documentAdmin: documentAdminOperations
})

const DocumentsAdmin = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentsAdminFC) as React.FC<OwnProps>

export default DocumentsAdmin
